<template>
  <div>
    <v-card
      elevation="0"
      class="rounded-lg"
      :key="reRender"
      :class="!isEdit ? 'mouse-cursor' : ''"
    >
      <div class="pa-4 d-flex align-center">
        <div class="flex-column" style="position: relative">
          <v-img
            v-if="!isEdit"
            class="rounded-lg no-filter"
            width="65"
            height="65"
            :src="
              getImage(avatar) ||
              require('@/app/user-module/assets/images/user-placeholder.png')
            "
            lazy-src="@/app/user-module/assets/images/user-placeholder.png"
            cover
          />

          <image-uploader
            v-else
            id="logo"
            :maxWidth="1024"
            :maxHeight="1024"
            :quality="0.9"
            :autoRotate="true"
            outputFormat="blob"
            :preview="false"
            :className="['fileinput', { 'fileinput--loaded': hasLogo }]"
            :capture="false"
            accept="image/*"
            doNotResize="['gif', 'svg']"
            @input="setLogo"
          >
            <label for="logo" slot="upload-label">
              <a>
                <v-img
                  v-if="logoUrl"
                  class="image-custom-preview"
                  :src="logoUrl"
                />

                <div v-if="!hasLogo" class="upload-caption gray1--text">
                  <v-img
                    v-if="avatar"
                    class="rounded-lg no-filter"
                    width="65"
                    height="65"
                    :src="
                      getImage(avatar) ||
                      require('@/app/user-module/assets/images/user-placeholder.png')
                    "
                    lazy-src="@/app/user-module/assets/images/user-placeholder.png"
                    cover
                  />

                  <v-img
                    v-else
                    class="rounded-lg no-filter"
                    width="65"
                    height="65"
                    lazy-src="@/app/user-module/assets/images/user-placeholder.png"
                    contain
                  />
                </div>
              </a>
            </label>
          </image-uploader>

          <float-btn
            v-if="
              isEditable &&
              (havePermissionUpdateEmployee || isAuthenticatedTrainer)
            "
            :isEdit="isEdit"
            @save="onClick"
            @edit="isEdit = true"
            :bottom="true"
            :loading="savingData"
          />
        </div>

        <div class="ps-4 d-flex flex-column text-start justify-center">
          <div
            class="
              d-flex
              flex-column flex-lg-row
              text-h6
              textLight--text
              text-ellipse-2
            "
          >
            <template v-if="isEditable && isEdit">
              <Editable2
                v-model="firstName"
                :isEdit="isEditable && isEdit"
                :validateRules="'required'"
                :placeholder="$t('user.firstName')"
                ref="firstName"
                class="me-2"
              />

              <Editable2
                v-model="lastName"
                :isEdit="isEditable && isEdit"
                :placeholder="$t('user.lastName')"
              />
            </template>

            <span v-else class="text-ellipse-1">
              {{
                `${textAbstract({
                  text: firstName,
                  maxLength: 20,
                  delimiter: "...",
                })} ${textAbstract({
                  text: lastName,
                  maxLength: 10,
                  delimiter: "...",
                })}`
              }}
            </span>
          </div>

          <div
            class="caption textLight--text text-ellipse-2"
            style="opacity: 0.5"
          >
            <Editable2
              v-model="designation"
              :isEdit="isEditable && isEdit"
              :placeholder="$t('user.designation')"
            />
          </div>

          <div class="caption textLight--text text-ellipse-2">
            <!-- <div v-if="!isEdit">
              {{
                company && company.name ? getLocalisedValue(company.name) : null
              }}
            </div> -->
            <v-autocomplete
              :readonly="!isEdit"
              append-icon=""
              hide-details
              v-model="organizationID"
              :items="organizations"
              item-value="_id"
              dense
              class="rounded-md text-body-2 ma-0 hide-underline"
              :filter="customLocalisedAutoCompleteFilter"
            >
              <template slot="selection" slot-scope="data">
                {{ getLocalisedValue(data.item.name) }}
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex justify-start">
                  {{ getLocalisedValue(item.name) }}
                </div>
              </template>
            </v-autocomplete>
          </div>
        </div>
      </div>
    </v-card>
    <v-row>
      <v-col>
        <CropperModal ref="cropperModal" @croppedData="croppedData" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Editable2 from "../../../core-module/components/ContentEditable2.vue";
import FloatBtn from "./FloatBtn.vue";
import CropperModal from "../../../core-module/components/CropperModal.vue";

import { updateEmployee } from "../../methods/updateEmployee";

import { hasPermission, authUser } from "@/app/user-module/helpers/authHelper";

export default {
  components: {
    Editable2,
    FloatBtn,
    CropperModal,
  },
  data() {
    return {
      savingData: false,
      userProfile: null,
      avatar: "",
      firstName: "",
      lastName: "",
      designation: "",
      company: "",
      organizationID: "",
      organizations: [],
      isEdit: false,
      isEditable: true,
      logoUrl: null,
      hasLogo: false,
      reRender: 0,
    };
  },
  props: {
    userData: {
      type: Object,
      default: () => {},
      required: true,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isPublicProfile() {
      return this.$route.name === "PublicProfile";
    },
    getProfileVariables() {
      return {
        id: this.$route.params.userId ? this.$route.params.userId : undefined,
      };
    },
    havePermissionUpdateEmployee() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["UPDATE_EMPLOYEE"],
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
    isAuthenticatedTrainer() {
      return authUser()?.subRoles?.find(x => x.name === "TRAINER") &&
        authUser()?._id === this.$route?.params?.userId
        ? true
        : false;
    },
  },
  watch: {
    userData: {
      immediate: true,
      handler() {
        this.userProfile = this.userData ? this.userData : null;
        this.resetFormData();
      },
      deep: true,
    },
  },
  methods: {
    resetFormData() {
      this.avatar = this.userProfile?.avatar;
      this.firstName = this.userProfile?.firstName;
      this.lastName = this.userProfile?.lastName;

      const roleKey = this.userProfile?.role?.toLowerCase();

      this.designation = this.userProfile?.[roleKey]?.designation;
      this.organizationID = this.userProfile?.[roleKey]?.organization?._id;

      this.setCompanyValue();

      this.reRender++;
    },
    async setCompanyValue() {
      this.organizations = await this.$apollo
        .query({
          query: require("@/app/organization-module/api/query/getOrganizations.gql"),
          variables: { limit: 0, skip: 0, filterInput: null },
        })
        .then(result => {
          if (
            result &&
            result.data &&
            result.data.getOrganizations &&
            result.data.getOrganizations.organizations
          ) {
            this.company = result.data.getOrganizations.organizations.find(
              obj => obj._id === this.organizationID
            );
            return result.data.getOrganizations.organizations;
          }
        });
    },
    async onClick() {
      let firstNameErr = (await this.$refs.firstName.checkIsValid())
        ? null
        : true;

      if (firstNameErr) return;

      this.savingData = true;
      this.isEdit = false;

      let saveData = {
        designation: this.designation ? this.designation : " ",
        organization: this.organizationID ? this.organizationID : null,
        user: {
          firstName: this.firstName ? this.firstName : null,
          lastName: this.lastName ? this.lastName : " ",
        },
      };

      if (this.avatar instanceof Blob) saveData.user.avatar = this.avatar;

      this.update(saveData).then(response => {
        if (response.error) {
          this.$emit("onError");
          this.resetFormData();
        } else if (response?.data) {
          if (response.data.updateEmployee?.user?.avatar) {
            this.avatar = response.data.updateEmployee.user.avatar;
          }
          this.hasLogo = false;
          this.logoUrl = null;
          this.isEdit = false;
          this.reRender++;
          this.$emit("update");
        }

        this.savingData = false;
      });
    },
    update(data) {
      return updateEmployee({
        id: this.userData?.employee?._id,
        input: data,
      });
    },
    setLogo(output) {
      this.hasLogo = true;
      var reader = new FileReader();
      reader.readAsDataURL(output);
      var self = this;

      reader.onloadend = function () {
        var base64data = reader.result;
        // self.categoryInput.url = base64data;
        self.logoUrl = base64data;
        self.$refs.cropperModal.show(self.logoUrl);
      };
      this.avatar = output;
    },
    croppedData(blobData, base64Data) {
      this.avatar = blobData;
      this.logoUrl = base64Data;
    },
  },
};
</script>
<style>
#fileInput {
  display: none;
}

#logo {
  display: none;
}
</style>
<style scoped>
.image-custom-preview {
  height: 65px !important;
  width: 65px !important;
  border-radius: 10px;
}
.hide-underline >>> .v-input__slot::before {
  border-style: none !important;
}
.hide-underline >>> .v-input__slot::after {
  border-style: none !important;
}
</style>
